import { useMemo } from "react";

import { SortOrder } from "@bridgesplit/utils";
import { FormInputType, DispatchType } from "@bridgesplit/react";

import { sortRows, splitPaginationProps, useTableController } from "./internal";
import { ApiPaginationProps, DataGridFilter, ManagedPaginationProps, TableColumn, TableRow } from "./types";

interface UseTableParams<TRow> {
    rows: TableRow<TRow>[] | undefined;
    columns: TableColumn<TRow>[];
    filters?: DataGridFilter<TRow>[];
    paginationProps: ApiPaginationProps | ManagedPaginationProps<TRow>;
}

interface UseTableResult<TRow> {
    /** Filtered → sorted → paginated rows. In API mode, returns the consumer's rows unchanged. */
    visibleRows: TableRow<TRow>[] | undefined;
    totalCount: number;
    isApi: boolean;
    sort: {
        order: SortOrder;
        orderBy: string;
        setOrder?: FormInputType<SortOrder>;
        setOrderBy?: FormInputType<string>;
    };
    page: {
        page: number;
        setPage: DispatchType<number>;
        maxPage: number;
        resetPage: () => void;
    };
    activeFilterCount: number;
    pageSize: number;
}

/**
 * Top-level table state. In managed mode, applies filter → sort → paginate
 * client-side. In API mode, trusts the consumer's `controller` and returns `rows`
 * unchanged.
 *
 * `safePage` clamps the slice on every render so a stale `page` after a filter
 * drop renders the last valid page instead of empty data. No effect writes the
 * clamp back to the controller — managed-mode state is internal (nothing reads
 * it externally), and API-mode consumers handle their own clamping via
 * `useClampPaginationOnCountChange`. `Pagination`'s next/prev clicks already
 * pass through a `Math.min(newPage, maxPage)` guard in the orchestrator.
 */
export function useTable<TRow>({
    rows,
    columns,
    filters,
    paginationProps
}: UseTableParams<TRow>): UseTableResult<TRow> {
    const { apiPaginationProps } = splitPaginationProps<TRow>(paginationProps);
    const isApi = !!apiPaginationProps;

    // useTableController is always called (hooks rules); API mode ignores its
    // state and uses the consumer's controller instead.
    const internalController = useTableController(paginationProps);
    const controller = isApi ? apiPaginationProps.controller : internalController;
    const { order, orderBy, setOrder, setOrderBy, page, setPage, resetPage } = controller;

    const pageSize = paginationProps.pageSize ?? 5;

    // ── filter → sort → paginate (managed mode only) ─────────────────────
    const filteredRows = useMemo(() => {
        if (!rows || isApi) return rows;
        const activePredicates = (filters ?? [])
            .map((f) => f.predicate)
            .filter((p): p is (row: TableRow<TRow>) => boolean => p !== null);
        if (activePredicates.length === 0) return rows;
        return rows.filter((row) => activePredicates.every((pred) => pred(row)));
    }, [rows, filters, isApi]);

    const sortedRows = useMemo(() => {
        if (!filteredRows || isApi) return filteredRows;
        return sortRows({ rows: filteredRows, columns, order, orderBy });
    }, [filteredRows, isApi, columns, order, orderBy]);

    const totalCount = isApi ? apiPaginationProps.totalDataCount ?? 0 : sortedRows?.length ?? 0;
    const maxPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1);
    const safePage = Math.min(page, maxPage);

    const visibleRows = useMemo(() => {
        if (!sortedRows) return sortedRows;
        if (isApi) return sortedRows;
        return sortedRows.slice(safePage * pageSize, safePage * pageSize + pageSize);
    }, [sortedRows, isApi, safePage, pageSize]);

    const activeFilterCount = useMemo(() => (filters ?? []).filter((f) => f.predicate != null).length, [filters]);

    return {
        visibleRows,
        totalCount,
        isApi,
        sort: { order, orderBy, setOrder, setOrderBy },
        page: { page: safePage, setPage, maxPage, resetPage },
        activeFilterCount,
        pageSize
    };
}
